import React, { useEffect, useState } from 'react';
import { Row, Col, InputNumber, Layout, Form, Input, TimePicker, Select, Button, notification, Checkbox, Divider, Upload, Switch, Table, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ColorPicker } from 'antd';
import AppHeader from '../../components/appHeader/AppHeader';
import { useNavigate } from 'react-router-dom';
import './adminStoreConfig.scss';
import '../../styles/responsive.scss';
import { serviceTimeFields } from '../../utils/serviceHours';

const { Option } = Select;

const AdminStoreConfig = () => {
  dayjs.extend(relativeTime);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);

  const [verificationOption, setVerificationOption] = useState('');
  const [storeInfoData, setStoreInfoData] = useState('');
  const [storeConfigData, setStoreConfigData] = useState('');
  const [form] = Form.useForm();
  const [storeId, setStoreId] = useState('');
  const [cusId, setCusId] = useState('');
  const [cusEmail, setCusEmail] = useState('');
  const [storeIconUrl, setStoreIconUrl] = useState('');
  const [imageError, setImageError] = useState(false);
  const [timeValues, setTimeValues] = useState({
    MondayStart: null,
    MondayEnd: null,
    TuesdayStart: null,
    TuesdayEnd: null,
    WednesdayStart: null,
    WednesdayEnd: null,
    ThursdayStart: null,
    ThursdayEnd: null,
    FridayStart: null,
    FridayEnd: null,
    SaturdayStart: null,
    SaturdayEnd: null,
    SundayStart: null,
    SundayEnd: null,

    MondayBreakStart: null,
    MondayBreakEnd: null,
    TuesdayBreakStart: null,
    TuesdayBreakEnd: null,
    WednesdayBreakStart: null,
    WednesdayBreakEnd: null,
    ThursdayBreakStart: null,
    ThursdayBreakEnd: null,
    FridayBreakStart: null,
    FridayBreakEnd: null,
    SaturdayBreakStart: null,
    SaturdayBreakEnd: null,
    SundayBreakStart: null,
    SundayBreakEnd: null,
    ...Object.fromEntries(serviceTimeFields.map((field) => [field, null])),
  });
  const [breakEnabled, setBreakEnabled] = useState({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false,
  });

  const [iconLoading, setIconLoading] = useState(false);
  const [iconUrl, setIconUrl] = useState('');
  const [iconLoadError, setIconLoadError] = useState(false);

  const [walkingPageSetup, setWalkingPageSetup] = useState('no');
  const [walkingPageImage, setWalkingPageImage] = useState(null);
  const [walkingPageColor, setWalkingPageColor] = useState('#000000');

  // New state variables for the additional inputs
  const [loadingPageColor, setLoadingPageColor] = useState('#000000'); // Default color
  const [loadingPagePercentage, setLoadingPagePercentage] = useState(0); // Default percentage

  // QR Order enable/disable state
  const [qrOrderEnabled, setQrOrderEnabled] = useState(true); // Default enabled

  // Order Success Pay Later Reminder state
  const [payLaterReminderEnabled, setPayLaterReminderEnabled] = useState(false);
  const [payLaterReminderText, setPayLaterReminderText] = useState('');

  // Order Success Pay First Reminder state
  const [payFirstReminderEnabled, setPayFirstReminderEnabled] = useState(false);
  const [payFirstReminderText, setPayFirstReminderText] = useState('');

  // Order Success Take-away Reminder state
  const [takeawayReminderEnabled, setTakeawayReminderEnabled] = useState(false);
  const [takeawayReminderText, setTakeawayReminderText] = useState('');

  // Promotion state
  const [promotionEnabled, setPromotionEnabled] = useState(false);
  const [promotionTitle, setPromotionTitle] = useState('');
  const [promotionDescription, setPromotionDescription] = useState('');
  const [promotionType, setPromotionType] = useState('percentage'); // 'percentage' or 'amount'
  const [promotionValue, setPromotionValue] = useState(0);
  const [promotionMinAmount, setPromotionMinAmount] = useState(0); // Minimum purchase amount
  const [crmPointsEnabled, setCrmPointsEnabled] = useState(false);
  const [crmEarnPointsPerDollar, setCrmEarnPointsPerDollar] = useState(1);
  const [crmRedeemPointsPerDollar, setCrmRedeemPointsPerDollar] = useState(100);
  const [crmActivation, setCrmActivation] = useState({ active: false, reason: 'CRM_NOT_CHECKED' });
  const crmActivationReasonText = (reason) => {
    if (reason === 'CRM_MEMBER_LEVEL_MISSING') return 'Member Level required';
    return reason || 'unknown';
  };
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [menuGroupSelectionEnabled, setMenuGroupSelectionEnabled] = useState(false);
  const [menuGroups, setMenuGroups] = useState([]);
  const [menuGroupImageVersion, setMenuGroupImageVersion] = useState(Date.now());

  const navigate = useNavigate();

  const handleVerificationOptionChange = (value) => {
    setVerificationOption(value);
    form.setFieldsValue({
      PayFirst: value === 'payFirst',
      UseLocation: value === 'useLocation',
      NoAuth: value === 'noAuth',
    });
  };

  // Handle QR Order toggle change and immediately send to backend
  const handleQrOrderToggle = async (checked) => {
    setQrOrderEnabled(checked);
    
    try {
      const token = sessionStorage.getItem('jwtToken');
      const config = {
        headers: {
          authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/updateQrOrderStatus`,
        { qrFunctionEnable: checked },
        config
      );

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: `QR Order has been ${checked ? 'enabled' : 'disabled'}`,
        });
      } else {
        notification.error({
          message: 'Error',
          description: 'Failed to update QR Order status',
        });
        // Revert the state if failed
        setQrOrderEnabled(!checked);
      }
    } catch (error) {
      console.error('Error updating QR Order status:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update QR Order status',
      });
      // Revert the state if failed
      setQrOrderEnabled(!checked);
    }
  };

  const fetchStoreConfig = async () => {
    const token = sessionStorage.getItem('jwtToken');
    const config = {
      headers: {
        authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/fetchStoreConfigAdmin`,
        config
      );

      if (response.status === 200) {
        const { storeInformation, storeConfig, iconUrl, crmPoints } = response.data;
        setCusEmail(storeInformation.adminEmail);
        setCusId(storeInformation.adminId);
        setStoreId(storeInformation.storeId);
        setStoreInfoData(storeInformation);
        setStoreConfigData(storeConfig);
        console.log(storeConfig);
        setStoreIconUrl(`${process.env.REACT_APP_SERVER_URL}/storeIcon/${storeInformation.storeId}`);
       
        setWalkingPageSetup(storeConfig.WalkingPageSetup===1 ? 'yes' : 'no');
        // setWalkingPageColor(storeConfig.walkingPageColor || '#000000');
        setLoadingPageColor(storeConfig.LoadingPageColor || '#000000'); // Set loading page color from backend
        setLoadingPagePercentage(storeConfig.LoadingPagePercentage || 0); // Set loading page percentage from backend
        setWalkingPageImage(null); // Assuming the image is not fetched here
        
        // Set QR Order enabled state from backend
        setQrOrderEnabled(storeConfig.QRFunctionEnable !== 0); // Assuming 1 is enabled, 0 is disabled
        
        // Set Pay Later Reminder state from backend
        setPayLaterReminderEnabled(storeConfig.PayLaterReminderEnable !== 0);
        setPayLaterReminderText(storeConfig.PayLaterReminderText || '');
        
        // Set Pay First Reminder state from backend
        setPayFirstReminderEnabled(storeConfig.PayFirstReminderEnable !== 0);
        setPayFirstReminderText(storeConfig.PayFirstReminderText || '');
        
        // Set Take-away Reminder state from backend
        setTakeawayReminderEnabled(storeConfig.TakeawayReminderEnable !== 0);
        setTakeawayReminderText(storeConfig.TakeawayReminderText || '');
        
        // Set Promotion state from backend
        setPromotionEnabled(storeConfig.PromotionEnable !== 0);
        setPromotionTitle(storeConfig.PromotionTitle || '');
        setPromotionDescription(storeConfig.PromotionDescription || '');
        setPromotionType(storeConfig.PromotionType || 'percentage');
        setPromotionValue(storeConfig.PromotionValue || 0);
        setPromotionMinAmount(storeConfig.PromotionMinAmount || 0);
        setCrmPointsEnabled(Boolean(crmPoints?.configuredEnabled));
        setCrmEarnPointsPerDollar(Number(crmPoints?.earnPointsPerDollar || 1));
        setCrmRedeemPointsPerDollar(Number(crmPoints?.redeemPointsPerDollar || 100));
        setCrmActivation(crmPoints?.activation || { active: false, reason: 'CRM_NOT_CHECKED' });
        setDeliveryEnabled(storeConfig.EnableDeliveryFunction !== 0);
        setMenuGroupSelectionEnabled(storeConfig.EnableMenuGroupSelection !== 0);
        
        form.setFieldsValue({
          storeName: storeInformation.storeName,
          storeAddress: storeInformation.storeAddress,
          storeContact: storeInformation.storePhone,
          SurchargeDescrip: storeConfig.SurchargeDescription,
          SurchargeRate: storeConfig.Surcharge,
          WeekendSurchargeRate: storeConfig.WeekendSurcharge,
          PublicSurcharge: storeConfig.PublicSurcharge,
          UsePickupSlip: storeConfig.UsePickupSlip,
          DisableOrderTracking: storeConfig.DisableOrderTracking,
          NeedCustomerInfo: storeConfig.NeedCustomerInfo,
          MaxItemInCart: storeConfig.MaxItemInCart,
          PayLaterReminderEnable: storeConfig.PayLaterReminderEnable !== 0,
          PayLaterReminderText: storeConfig.PayLaterReminderText || '',
          PayFirstReminderEnable: storeConfig.PayFirstReminderEnable !== 0,
          PayFirstReminderText: storeConfig.PayFirstReminderText || '',
          TakeawayReminderEnable: storeConfig.TakeawayReminderEnable !== 0,
          TakeawayReminderText: storeConfig.TakeawayReminderText || '',
          
          PromotionEnable: storeConfig.PromotionEnable !== 0,
          PromotionTitle: storeConfig.PromotionTitle || '',
          PromotionDescription: storeConfig.PromotionDescription || '',
          PromotionType: storeConfig.PromotionType || 'percentage',
          PromotionValue: storeConfig.PromotionValue || 0,
          PromotionMinAmount: storeConfig.PromotionMinAmount || 0,
          CrmPointsEnabled: Boolean(crmPoints?.configuredEnabled),
          CrmEarnPointsPerDollar: Number(crmPoints?.earnPointsPerDollar || 1),
          CrmRedeemPointsPerDollar: Number(crmPoints?.redeemPointsPerDollar || 100),
          
          ShowPriceInCustomerMenu: storeConfig.ShowPriceInCustomerMenu !== 0,
          EnableSharedCart: storeConfig.EnableSharedCart !== 0,
          RequireDineInGuestCount: storeConfig.RequireDineInGuestCount !== 0,
          EnableDeliveryFunction: storeConfig.EnableDeliveryFunction !== 0,
          DeliveryBaseFee: storeConfig.DeliveryBaseFee || 0,
          DeliveryFeePerKm: storeConfig.DeliveryFeePerKm || 0,
          DeliveryRangeKm: storeConfig.DeliveryRangeKm || 0,
          DeliveryMinimumOrderAmount: storeConfig.DeliveryMinimumOrderAmount || 0,
          EnableMenuGroupSelection: storeConfig.EnableMenuGroupSelection !== 0,
       
          walkingPageColor: storeConfig.LoadingPageColor? storeConfig.LoadingPageColor: '#000000',

          walkingPageSetup: storeConfig.WalkingPageSetup === 1 ? 'yes' : 'no',
         
          loadingPageColorInput: storeConfig.LoadingPageColor? storeConfig.LoadingPageColor: '#000000', 
          loadingPagePercentage: storeConfig.LoadingPagePercentage || 0, 
        });

        if (storeConfig.PayFirst) {
          setVerificationOption('payFirst');
          form.setFieldsValue({ verificationOption: 'payFirst' });
        } else if (storeConfig.UseLocation) {
          setVerificationOption('useLocation');
          form.setFieldsValue({ verificationOption: 'useLocation' });
        } else if (storeConfig.NoAuth) {
          setVerificationOption('noAuth');
          form.setFieldsValue({ verificationOption: 'noAuth' });
        }

        const newTimeValues = {
          MondayStart: storeConfig.MondayStart
            ? dayjs(storeConfig.MondayStart, 'HH:mm')
            : null,
          MondayEnd: storeConfig.MondayEnd
            ? dayjs(storeConfig.MondayEnd, 'HH:mm')
            : null,
          TuesdayStart: storeConfig.TuesdayStart
            ? dayjs(storeConfig.TuesdayStart, 'HH:mm')
            : null,
          TuesdayEnd: storeConfig.TuesdayEnd
            ? dayjs(storeConfig.TuesdayEnd, 'HH:mm')
            : null,
          WednesdayStart: storeConfig.WednesdayStart
            ? dayjs(storeConfig.WednesdayStart, 'HH:mm')
            : null,
          WednesdayEnd: storeConfig.WednesdayEnd
            ? dayjs(storeConfig.WednesdayEnd, 'HH:mm')
            : null,
          ThursdayStart: storeConfig.ThursdayStart
            ? dayjs(storeConfig.ThursdayStart, 'HH:mm')
            : null,
          ThursdayEnd: storeConfig.ThursdayEnd
            ? dayjs(storeConfig.ThursdayEnd, 'HH:mm')
            : null,
          FridayStart: storeConfig.FridayStart
            ? dayjs(storeConfig.FridayStart, 'HH:mm')
            : null,
          FridayEnd: storeConfig.FridayEnd
            ? dayjs(storeConfig.FridayEnd, 'HH:mm')
            : null,
          SaturdayStart: storeConfig.SaturdayStart
            ? dayjs(storeConfig.SaturdayStart, 'HH:mm')
            : null,
          SaturdayEnd: storeConfig.SaturdayEnd
            ? dayjs(storeConfig.SaturdayEnd, 'HH:mm')
            : null,
          SundayStart: storeConfig.SundayStart
            ? dayjs(storeConfig.SundayStart, 'HH:mm')
            : null,
          SundayEnd: storeConfig.SundayEnd
            ? dayjs(storeConfig.SundayEnd, 'HH:mm')
            : null,

          MondayBreakStart: storeConfig.MondayBreakStart
            ? dayjs(storeConfig.MondayBreakStart, 'HH:mm')
            : null,
          MondayBreakEnd: storeConfig.MondayBreakEnd
            ? dayjs(storeConfig.MondayBreakEnd, 'HH:mm')
            : null,
          TuesdayBreakStart: storeConfig.TuesdayBreakStart
            ? dayjs(storeConfig.TuesdayBreakStart, 'HH:mm')
            : null,
          TuesdayBreakEnd: storeConfig.TuesdayBreakEnd
            ? dayjs(storeConfig.TuesdayBreakEnd, 'HH:mm')
            : null,
          WednesdayBreakStart: storeConfig.WednesdayBreakStart
            ? dayjs(storeConfig.WednesdayBreakStart, 'HH:mm')
            : null,
          WednesdayBreakEnd: storeConfig.WednesdayBreakEnd
            ? dayjs(storeConfig.WednesdayBreakEnd, 'HH:mm')
            : null,
          ThursdayBreakStart: storeConfig.ThursdayBreakStart
            ? dayjs(storeConfig.ThursdayBreakStart, 'HH:mm')
            : null,
          ThursdayBreakEnd: storeConfig.ThursdayBreakEnd
            ? dayjs(storeConfig.ThursdayBreakEnd, 'HH:mm')
            : null,
          FridayBreakStart: storeConfig.FridayBreakStart
            ? dayjs(storeConfig.FridayBreakStart, 'HH:mm')
            : null,
          FridayBreakEnd: storeConfig.FridayBreakEnd
            ? dayjs(storeConfig.FridayBreakEnd, 'HH:mm')
            : null,
          SaturdayBreakStart: storeConfig.SaturdayBreakStart
            ? dayjs(storeConfig.SaturdayBreakStart, 'HH:mm')
            : null,
          SaturdayBreakEnd: storeConfig.SaturdayBreakEnd
            ? dayjs(storeConfig.SaturdayBreakEnd, 'HH:mm')
            : null,
          SundayBreakStart: storeConfig.SundayBreakStart
            ? dayjs(storeConfig.SundayBreakStart, 'HH:mm')
            : null,
          SundayBreakEnd: storeConfig.SundayBreakEnd
            ? dayjs(storeConfig.SundayBreakEnd, 'HH:mm')
            : null,
          ...Object.fromEntries(serviceTimeFields.map((field) => [
            field,
            storeConfig[field] ? dayjs(storeConfig[field], 'HH:mm') : null,
          ])),
        };

        const newBreakEnabled = {
          Monday:
            !!storeConfig.MondayBreakStart && !!storeConfig.MondayBreakEnd,
          Tuesday:
            !!storeConfig.TuesdayBreakStart && !!storeConfig.TuesdayBreakEnd,
          Wednesday:
            !!storeConfig.WednesdayBreakStart &&
            !!storeConfig.WednesdayBreakEnd,
          Thursday:
            !!storeConfig.ThursdayBreakStart && !!storeConfig.ThursdayBreakEnd,
          Friday:
            !!storeConfig.FridayBreakStart && !!storeConfig.FridayBreakEnd,
          Saturday:
            !!storeConfig.SaturdayBreakStart && !!storeConfig.SaturdayBreakEnd,
          Sunday:
            !!storeConfig.SundayBreakStart && !!storeConfig.SundayBreakEnd,
        };

        setBreakEnabled(newBreakEnabled);
        setTimeValues(newTimeValues);
        form.setFieldsValue(newTimeValues);
      } else {
        navigate('/qr_order/adminLogin');
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      navigate('/qr_order/adminLogin');
    }
  };

  const fetchMenuGroups = async () => {
    try {
      const token = sessionStorage.getItem('jwtToken');
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/admin-menu-group-selection`,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setMenuGroups((response.data.menuGroups || []).map((group) => ({
          ...group,
          DisplayName: group.DisplayName || '',
          MenuGroupPrice: Number(group.MenuGroupPrice) || 0,
          Popular: Number(group.Popular) === 1,
          MenuGroupSelectionEnabled: group.MenuGroupSelectionEnabled === undefined
            ? true
            : Number(group.MenuGroupSelectionEnabled) === 1,
        })));
      }
    } catch (error) {
      console.error('Error fetching menu groups:', error);
      notification.error({ message: 'Failed to fetch menu groups' });
    }
  };

  useEffect(() => {
    fetchStoreConfig();
    fetchMenuGroups();
  }, []);

  useEffect(() => {
    if (storeId) {
      setIconUrl(
        `https://onlineorder.enrichpos.com.au:5049/storeIcon/${storeId}`
      );
    }
  }, [storeId]);

  // const handleColorChange = (value) => {
  //   if (value && value.metaColor) {
  //     const hexColor = convertToHex(value.rgb);
  //     console.log(hexColor);
  //     setWalkingPageColor(hexColor);
  //     form.setFieldValue('walkingPageColor', hexColor);
  //   }
  // };

  // const convertToHex = (color) => {
  //   if (
  //     !color ||
  //     typeof color.r === 'undefined' ||
  //     typeof color.g === 'undefined' ||
  //     typeof color.b === 'undefined'
  //   ) {
  //     // 当 color 对象不符合预期时，返回默认颜色或者处理错误
  //     console.warn("Invalid color object, returning default #000000");
  //     return '#000000'; // 返回默认黑色
  //   }

  //   // 如果 color 对象存在并且有 r, g, b 属性，则继续转换
  //   const { r, g, b } = color;
  //   return `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b))
  //     .toString(16)
  //     .slice(1)
  //     .toUpperCase()}`;
  // };

  const handleIconChange = ({ file }) => {
    if (!file) {
      setIconLoading(false);
      return;
    }

    setIconLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      setIconUrl(e.target.result);
      setIconLoading(false);
      setIconLoadError(false);
    };
    reader.onerror = () => {
      setIconLoading(false);
      setIconLoadError(true);
      notification.error({ message: 'File read error' });
    };
    reader.readAsDataURL(file);
  };

  const uploadButton = (
    <div>
      {iconLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const resizeMenuGroupImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const targetWidth = 320;
          const targetHeight = 200;
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          const ctx = canvas.getContext('2d');
          const sourceRatio = img.width / img.height;
          const targetRatio = targetWidth / targetHeight;
          let sourceWidth = img.width;
          let sourceHeight = img.height;
          let sourceX = 0;
          let sourceY = 0;

          if (sourceRatio > targetRatio) {
            sourceWidth = img.height * targetRatio;
            sourceX = (img.width - sourceWidth) / 2;
          } else {
            sourceHeight = img.width / targetRatio;
            sourceY = (img.height - sourceHeight) / 2;
          }

          ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
    });
  };

  const convertToHexWithAlpha = (color) => {
    // 确保 r, g, b 是有效值，并且位于 [0, 255] 之间
    const r = Math.round(color.r);
    const g = Math.round(color.g);
    const b = Math.round(color.b);

    // 确保 a 是介于 0 和 1 之间的浮点数，将其转换为 0-255 之间的整数
    const a = Math.round(color.a * 255);

    // 转换 RGB 和 Alpha 为十六进制格式
    const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)
      .toUpperCase()}`;

    // 转换 Alpha 为两位的十六进制，并与 RGB 部分拼接
    const alphaHex = a.toString(16).padStart(2, '0').toUpperCase();

    return `${hexColor}${alphaHex}`;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (values.SurchargeRate == null || values.WeekendSurchargeRate == null || values.PublicSurcharge == null) {
        notification.error({
          message: 'Save Failed',
          description:
            'Surcharge Rate or Weekend Surcharge Rate cannot be empty.',
        });
        return;
      }

      const days = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ];
      for (let day of days) {
        for (const service of ['Takeaway', 'Delivery']) {
          const start = values[`${day}${service}Start`];
          const end = values[`${day}${service}End`];
          if (Boolean(start) !== Boolean(end)) {
            notification.error({
              message: `${day}: ${service} hours are incomplete`,
              description: 'Set both start and end time, or leave both empty for no additional restriction.',
            });
            return;
          }
        }
        if (breakEnabled[day]) {
          const start = dayjs(values[`${day}Start`], 'HH:mm');
          const end = dayjs(values[`${day}End`], 'HH:mm');
          const breakStart = dayjs(values[`${day}BreakStart`], 'HH:mm');
          const breakEnd = dayjs(values[`${day}BreakEnd`], 'HH:mm');

          if (breakStart && breakEnd) {
            // 检查是否跨午夜营业
            const isOvernightBusiness = start.isAfter(end);
            // 检查是否跨午夜休息
            const isOvernightBreak = breakStart.isAfter(breakEnd);
            
            let breakTimesValid = true;
            
            if (isOvernightBusiness) {
              // 跨午夜营业时间的验证逻辑
              if (isOvernightBreak) {
                // 跨午夜营业 + 跨午夜休息：需要确保休息时间在营业时间内
                // 这种情况比较复杂，暂时允许，由用户自行确保合理性
                breakTimesValid = true;
              } else {
                // 跨午夜营业 + 正常休息：休息时间应该在当天的营业时间内，或者在次日的营业时间内
                const breakInFirstPeriod = (breakStart.isSameOrAfter(start) && breakEnd.isSameOrAfter(start)) ||
                                         (breakStart.isSameOrBefore(end) && breakEnd.isSameOrBefore(end));
                breakTimesValid = breakInFirstPeriod;
              }
            } else {
              // 正常营业时间的验证逻辑
              if (isOvernightBreak) {
                // 正常营业 + 跨午夜休息：不合理，休息时间不能跨午夜而营业时间不跨午夜
                breakTimesValid = false;
              } else {
                // 正常营业 + 正常休息：传统验证逻辑
                breakTimesValid = !(
                  breakStart.isSameOrBefore(start) ||
                  breakEnd.isSameOrAfter(end)
                );
              }
            }
            
            if (!breakTimesValid) {
              notification.error({
                message: `${day}: Break times are incorrect`,
                description: 'Break times must be within business hours. Please check your timetable.',
              });
              return;
            }
          }
        }
      }

      if (values.verificationOption === 'payFirst') {
        values.PayFirst = 1;
        values.UseLocation = 0;
        values.NoAuth = 0;
      } else if (values.verificationOption === 'useLocation') {
        values.PayFirst = 0;
        values.UseLocation = 1;
        values.NoAuth = 0;
      } else if (values.verificationOption === 'noAuth') {
        values.PayFirst = 0;
        values.UseLocation = 0;
        values.NoAuth = 1;
      }

      // Update walking page settings
      if (walkingPageSetup === 'yes') {
        values.WalkingPageSetup = 1;
      } else {
        values.WalkingPageSetup = 0;
      }

      if (values.MaxItemInCart == null) {
        notification.error({
          message: 'Save Failed',
          description: 'Max Item in Cart cannot be empty.',
        });
        return;
      }

    
      values.WalkingPageImage = walkingPageImage
        ? await getBase64(walkingPageImage)
        : null;
      

      // Include new loading page settings
      values.LoadingPageColor = loadingPageColor;
      values.LoadingPagePercentage = loadingPagePercentage;
      
      // Include QR Order enabled status
      values.QRFunctionEnable = qrOrderEnabled;

      // Include Pay Later Reminder settings
      values.PayLaterReminderEnable = payLaterReminderEnabled ? 1 : 0;
      values.PayLaterReminderText = payLaterReminderText;

      // Include Pay First Reminder settings
      values.PayFirstReminderEnable = payFirstReminderEnabled ? 1 : 0;
      values.PayFirstReminderText = payFirstReminderText;

      // Include Take-away Reminder settings
      values.TakeawayReminderEnable = takeawayReminderEnabled ? 1 : 0;
      values.TakeawayReminderText = takeawayReminderText;

      // Include Promotion settings
      values.PromotionEnable = promotionEnabled ? 1 : 0;
      values.PromotionTitle = promotionTitle;
      values.PromotionDescription = promotionDescription;
      values.PromotionType = promotionType;
      values.PromotionValue = promotionValue;
      values.PromotionMinAmount = promotionMinAmount;

      values.CrmPointsEnabled = crmPointsEnabled ? 1 : 0;
      values.CrmEarnPointsPerDollar = crmEarnPointsPerDollar;
      values.CrmRedeemPointsPerDollar = crmRedeemPointsPerDollar;

      // Include Customer Menu Show Price setting
      values.ShowPriceInCustomerMenu = values.ShowPriceInCustomerMenu ? 1 : 0;
      values.EnableSharedCart = values.EnableSharedCart ? 1 : 0;
      values.RequireDineInGuestCount = values.RequireDineInGuestCount ? 1 : 0;
      values.EnableDeliveryFunction = values.EnableDeliveryFunction ? 1 : 0;
      values.EnableMenuGroupSelection = menuGroupSelectionEnabled ? 1 : 0;
      values.DeliveryBaseFee = values.EnableDeliveryFunction ? (values.DeliveryBaseFee || 0) : 0;
      values.DeliveryFeePerKm = values.EnableDeliveryFunction ? (values.DeliveryFeePerKm || 0) : 0;
      values.DeliveryRangeKm = values.EnableDeliveryFunction ? (values.DeliveryRangeKm || 0) : 0;
      values.DeliveryMinimumOrderAmount = values.EnableDeliveryFunction ? (values.DeliveryMinimumOrderAmount || 0) : 0;

      const invalidMenuGroup = menuGroups.find((group) => (
        !String(group.DisplayName || '').trim() || Number(group.MenuGroupPrice) < 0
      ));
      if (menuGroupSelectionEnabled && invalidMenuGroup) {
        notification.error({
          message: 'Save Failed',
          description: 'Display Name is required and price cannot be negative.',
        });
        return;
      }

    
      const storeConfig = {
        ...values,
        ...Object.fromEntries(serviceTimeFields.map((field) => [
          field,
          values[field] ? values[field].format('HH:mm') : null,
        ])),
        MondayStart: values.MondayStart
          ? values.MondayStart.format('HH:mm')
          : null,
        MondayEnd: values.MondayEnd ? values.MondayEnd.format('HH:mm') : null,
        TuesdayStart: values.TuesdayStart
          ? values.TuesdayStart.format('HH:mm')
          : null,
        TuesdayEnd: values.TuesdayEnd ? values.TuesdayEnd.format('HH:mm') : null,
        WednesdayStart: values.WednesdayStart
          ? values.WednesdayStart.format('HH:mm')
          : null,
        WednesdayEnd: values.WednesdayEnd
          ? values.WednesdayEnd.format('HH:mm')
          : null,
        ThursdayStart: values.ThursdayStart
          ? values.ThursdayStart.format('HH:mm')
          : null,
        ThursdayEnd: values.ThursdayEnd
          ? values.ThursdayEnd.format('HH:mm')
          : null,
        FridayStart: values.FridayStart ? values.FridayStart.format('HH:mm') : null,
        FridayEnd: values.FridayEnd ? values.FridayEnd.format('HH:mm') : null,
        SaturdayStart: values.SaturdayStart
          ? values.SaturdayStart.format('HH:mm')
          : null,
        SaturdayEnd: values.SaturdayEnd
          ? values.SaturdayEnd.format('HH:mm')
          : null,
        SundayStart: values.SundayStart ? values.SundayStart.format('HH:mm') : null,
        SundayEnd: values.SundayEnd ? values.SundayEnd.format('HH:mm') : null,

        MondayBreakStart: values.MondayBreakStart
          ? values.MondayBreakStart.format('HH:mm')
          : null,
        MondayBreakEnd: values.MondayBreakEnd
          ? values.MondayBreakEnd.format('HH:mm')
          : null,
        TuesdayBreakStart: values.TuesdayBreakStart
          ? values.TuesdayBreakStart.format('HH:mm')
          : null,
        TuesdayBreakEnd: values.TuesdayBreakEnd
          ? values.TuesdayBreakEnd.format('HH:mm')
          : null,
        WednesdayBreakStart: values.WednesdayBreakStart
          ? values.WednesdayBreakStart.format('HH:mm')
          : null,
        WednesdayBreakEnd: values.WednesdayBreakEnd
          ? values.WednesdayBreakEnd.format('HH:mm')
          : null,
        ThursdayBreakStart: values.ThursdayBreakStart
          ? values.ThursdayBreakStart.format('HH:mm')
          : null,
        ThursdayBreakEnd: values.ThursdayBreakEnd
          ? values.ThursdayBreakEnd.format('HH:mm')
          : null,
        FridayBreakStart: values.FridayBreakStart
          ? values.FridayBreakStart.format('HH:mm')
          : null,
        FridayBreakEnd: values.FridayBreakEnd
          ? values.FridayBreakEnd.format('HH:mm')
          : null,
        SaturdayBreakStart: values.SaturdayBreakStart
          ? values.SaturdayBreakStart.format('HH:mm')
          : null,
        SaturdayBreakEnd: values.SaturdayBreakEnd
          ? values.SaturdayBreakEnd.format('HH:mm')
          : null,
        SundayBreakStart: values.SundayBreakStart
          ? values.SundayBreakStart.format('HH:mm')
          : null,
        SundayBreakEnd: values.SundayBreakEnd
          ? values.SundayBreakEnd.format('HH:mm')
          : null,

      
       
      };

      const token = sessionStorage.getItem('jwtToken');
      const configHeader = {
        headers: {
          authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/updateStoreConfig`,
        { storeConfig, iconUrl },
        configHeader
      );
      if (response.status === 200) {
        if (menuGroupSelectionEnabled) {
          await axios.post(
            `${process.env.REACT_APP_SERVER_URL}/admin-menu-group-selection`,
            { menuGroups },
            configHeader
          );
        }
        notification.success({ message: 'Updated Successfully' });
        fetchStoreConfig();
        fetchMenuGroups();
      } else {
        notification.error({ message: 'Update Failed' });
      }
    } catch (error) {
      console.error('Save Error:', error);
      notification.error({
        message: 'Update Failed',
        description: error.response?.data?.message || error.response?.data?.reason || error.message,
      });
    }
  };

  const handleTimeChange = (day, period, time) => {
    setTimeValues((prev) => ({
      ...prev,
      [`${day}${period}`]: time,
    }));
    form.setFieldsValue({
      [`${day}${period}`]: time ? dayjs(time, 'HH:mm') : null,
    });
  };

  const updateMenuGroupField = (code, field, value) => {
    setMenuGroups((prev) => prev.map((group) => (
      group.Code === code ? { ...group, [field]: value } : group
    )));
  };

  const uploadMenuGroupImage = async (code, file) => {
    if (!file.type.startsWith('image/')) {
      notification.error({ message: 'Only image files can be uploaded' });
      return false;
    }
    try {
      const token = sessionStorage.getItem('jwtToken');
      const image = await resizeMenuGroupImage(file);
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/upload-menu-group-image`,
        { code, image },
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );
      setMenuGroupImageVersion(Date.now());
      notification.success({ message: 'Menu group image uploaded' });
    } catch (error) {
      console.error('Error uploading menu group image:', error);
      notification.error({ message: 'Failed to upload menu group image' });
    }
    return false;
  };

  const deleteMenuGroupImage = async (code) => {
    try {
      const token = sessionStorage.getItem('jwtToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/delete-menu-group-image`,
        { code },
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );
      setMenuGroupImageVersion(Date.now());
      notification.success({ message: 'Menu group image deleted' });
    } catch (error) {
      console.error('Error deleting menu group image:', error);
      notification.error({ message: 'Failed to delete menu group image' });
    }
  };

  const menuGroupColumns = [
    {
      title: 'Code',
      dataIndex: 'Code',
      key: 'Code',
      width: 90,
    },
    {
      title: 'Menu Group',
      dataIndex: 'Description',
      key: 'Description',
      width: 160,
    },
    {
      title: 'Display Name',
      dataIndex: 'DisplayName',
      key: 'DisplayName',
      render: (_, record) => (
        <Input
          value={record.DisplayName}
          maxLength={50}
          onChange={(e) => updateMenuGroupField(record.Code, 'DisplayName', e.target.value)}
        />
      ),
    },
    {
      title: 'Price (0 = hidden)',
      dataIndex: 'MenuGroupPrice',
      key: 'MenuGroupPrice',
      width: 180,
      render: (_, record) => (
        <InputNumber
          min={0}
          precision={2}
          value={record.MenuGroupPrice}
          style={{ width: '100%' }}
          onChange={(value) => updateMenuGroupField(record.Code, 'MenuGroupPrice', value || 0)}
        />
      ),
    },
    {
      title: 'Popular',
      dataIndex: 'Popular',
      key: 'Popular',
      width: 110,
      render: (_, record) => (
        <Checkbox
          checked={Boolean(record.Popular)}
          onChange={(e) => updateMenuGroupField(record.Code, 'Popular', e.target.checked)}
        />
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'MenuGroupSelectionEnabled',
      key: 'MenuGroupSelectionEnabled',
      width: 110,
      render: (_, record) => (
        <Checkbox
          checked={record.MenuGroupSelectionEnabled !== false}
          onChange={(e) => updateMenuGroupField(record.Code, 'MenuGroupSelectionEnabled', e.target.checked)}
        />
      ),
    },
    {
      title: 'Image (Recommended 320 x 200)',
      key: 'image',
      width: 260,
      render: (_, record) => (
        <div className="menu-group-image-cell">
          <img
            className="menu-group-image-preview"
            src={`${process.env.REACT_APP_SERVER_URL}/images/${storeId}/menuGroups/${record.Code}?v=${menuGroupImageVersion}`}
            alt={record.Description}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/img/blank.jpeg';
            }}
          />
          <Upload
            showUploadList={false}
            accept=".png,image/png,image/*"
            beforeUpload={(file) => uploadMenuGroupImage(record.Code, file)}
          >
            <Button size="small">Upload / Replace</Button>
          </Upload>
          <Popconfirm
            title="Delete this image?"
            onConfirm={() => deleteMenuGroupImage(record.Code)}
          >
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Layout className="layout" style={{ minHeight: '100vh' }}>
       <AppHeader email={cusEmail} cusId={cusId}/>
      <div className="table_list">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h2>Store Set Up</h2>
        </div>

        <div className="admin_store_config">
          <Divider orientation="left" orientationMargin="0">
            General Store Information
          </Divider>

          <Form form={form} layout="vertical" initialValues={storeConfigData}>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name="storeName" label="Store Name">
                  <Input style={{ maxWidth: '100%' }}></Input>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="QR Order Status">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                      checked={qrOrderEnabled}
                      onChange={handleQrOrderToggle}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                    <span style={{ color: qrOrderEnabled ? '#52c41a' : '#ff4d4f' }}>
                      {qrOrderEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Store Icon (Recommended Size: 235 x 170)">
                  <Row gutter={16} align="middle" style={{ flexWrap: 'wrap' }}>
                    <Col>
                      {storeIconUrl && (
                        <img
                          src={storeIconUrl}
                          alt="icon"
                          style={{ width: '100px', marginRight: '16px', marginBottom: '8px' }}
                          onError={() => setIconLoadError(true)}
                        />
                      )}
                    </Col>
                    <Col>
                      <Upload
                        name="icon"
                        listType="picture-card"
                        className="avatar-uploader"
                        showUploadList={false}
                        beforeUpload={() => false}
                        accept=".jpg,.jpeg"
                        onChange={handleIconChange}
                      >
                        {iconUrl && !iconLoadError ? (
                          <img
                            src={iconUrl}
                            alt="icon"
                            style={{ width: '100%' }}
                            onError={() => setIconLoadError(true)}
                          />
                        ) : (
                          uploadButton
                        )}
                      </Upload>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name="storeAddress" label="Store Address">
                  <Input style={{ maxWidth: '100%' }}></Input>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name="storeContact" label="Store Contact Number">
                  <Input style={{ maxWidth: '100%' }}></Input>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name="SurchargeDescrip" label="Surcharge Description">
                  <Input style={{ maxWidth: '100%' }}></Input>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="SurchargeRate"
                  label="Surcharge Rate (%)"
                  initialValue={storeConfigData.Surcharge}
                >
                  <InputNumber style={{ maxWidth: '100%' }}></InputNumber>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="WeekendSurchargeRate"
                  label="Weekend Surcharge Rate (%)"
                >
                  <InputNumber style={{ maxWidth: '100%' }}></InputNumber>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="PublicSurcharge"
                  label="Public Holiday Surcharge Rate (%)"
                >
                  <InputNumber style={{ maxWidth: '100%' }}></InputNumber>
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left" orientationMargin="0">
              Landing Page Setup
            </Divider>
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name="walkingPageSetup" label="Enable Landing Page">
                  <Select
                    style={{ width: '100%', maxWidth: 240 }}
                    onChange={(value) => setWalkingPageSetup(value)}
                    value={walkingPageSetup}
                  >
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {walkingPageSetup === 'yes' && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                  {/* show langding page image ${process.env.REACT_APP_SERVER_URL}/updateStoreConfig if exist */}
                  {!imageError && 
                 (
                  <img
                    src={`${process.env.REACT_APP_SERVER_URL}/storeLandingImage/${storeId}`}
                  
                    alt="landing page"
                    style={{ width:'100px', marginRight: '16px', marginBottom: '8px' }}
                    onError={() => setImageError(true)} // 如果发生错误，将错误状态设置为 true
                  />
                )}
                 <Form.Item
                      label="Upload Landing Page Image (Recommended Size: 900 x 1600)"
                      name="walkingPageImage"
                    >
                      <Upload
                        name="walkingPageImage"
                        listType="picture-card"
                        showUploadList={false}
                        beforeUpload={() => false}
                        accept=".jpg,.jpeg"
                        onChange={({ file }) => setWalkingPageImage(file)}
                      >
                        {walkingPageImage ? (
                          <img
                            src={URL.createObjectURL(walkingPageImage)}
                            alt="Walking Page"
                            style={{ width: '100%' }}
                          />
                        ) : (
                          <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                          </div>
                        )}
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
                {/* <Row gutter={10}>
                
                  <Col span={8}>
                    <Form.Item
                      name="loadingPageColorInput"
                      label="Primary Color"
                      rules={[
                        {
                          pattern: /^#([0-9A-F]{3}){1,2}$/i,
                          message: 'Please enter a valid hex color code!',
                        },
                      ]}
                    >
                      <Input
                        placeholder="#000000"
                        
                        value={loadingPageColor}
                        style={{ width: 240 }}
                        onChange={(e) => {
                          const color = e.target.value;
                          setLoadingPageColor(color);
                          form.setFieldsValue({ loadingPageColorInput: color });
                        }}
                      />
                    </Form.Item>
                  </Col>
              
                  <Col span={12}>
                    <Form.Item
                      name="loadingPagePercentage"
                      label="Transparency (%)"
                      rules={[
                        {
                          type: 'number',
                          min: 0,
                          max: 100,
                          message: 'Please enter a value between 0 and 100!',
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        value={loadingPagePercentage}
                        onChange={(value) => {
                          setLoadingPagePercentage(value);
                          form.setFieldsValue({ loadingPagePercentage: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row> */}
                {/* <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Select Button Color"
                      name="walkingPageColor"
                    >
                      <ColorPicker
                        value={walkingPageColor}
                        onChangeComplete={(color) => {
                          setWalkingPageColor(color);
                          form.setFieldsValue({ walkingPageColor: color });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row> */}
              </>
            )}

            <Divider orientation="left" orientationMargin="0">
              Customer Order Verification Options
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name="verificationOption" label="Verification Option">
                  <Select
                    style={{ width: '100%', maxWidth: 240 }}
                    onChange={handleVerificationOptionChange}
                    value={verificationOption}
                  >
                    <Option value="payFirst">Pay First</Option>
                    <Option value="useLocation">Capture User Location</Option>
                    <Option value="noAuth">No Authentication</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <div className="store-open-time">
              <Divider orientation="left" orientationMargin="0">
                Store Business Hours
              </Divider>

              <div className="store-open-time-content">
                <Row gutter={[8, 8]} className="service-hours-heading">
                  <Col xs={24} md={8}><b>Store Hours</b></Col>
                  <Col xs={24} md={8}><b>Takeaway Hours</b></Col>
                  {deliveryEnabled && <Col xs={24} md={8}><b>Delivery Hours</b></Col>}
                </Row>
                {[
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                  'Sunday',
                ].map((day) => (
                  <Form.Item label={day} key={day} className="business-hours-day">
                    <Row gutter={[8, 8]} align="middle">
                      <Col span={24}>
                        <Checkbox
                          checked={breakEnabled[day]}
                          onChange={(e) =>
                            setBreakEnabled((prev) => ({
                              ...prev,
                              [day]: e.target.checked,
                            }))
                          }
                        >
                          Set Break Time
                        </Checkbox>
                      </Col>
                      <Col xs={24} md={8}>
                        <Row gutter={[8, 8]}>
                          <Col span={12}>
                            <Form.Item name={`${day}Start`} noStyle>
                              <TimePicker
                                value={timeValues[`${day}Start`]}
                                format="HH:mm"
                                placeholder="Start time"
                                onChange={(time) => handleTimeChange(day, 'Start', time)}
                              />
                            </Form.Item>
                          </Col>
                          {breakEnabled[day] && (
                            <Col span={12}>
                              <Form.Item name={`${day}BreakStart`} noStyle>
                                <TimePicker
                                  value={timeValues[`${day}BreakStart`]}
                                  format="HH:mm"
                                  placeholder="Break start"
                                  onChange={(time) => handleTimeChange(day, 'BreakStart', time)}
                                />
                              </Form.Item>
                            </Col>
                          )}
                          {breakEnabled[day] && (
                            <Col span={12}>
                              <Form.Item name={`${day}BreakEnd`} noStyle>
                                <TimePicker
                                  value={timeValues[`${day}BreakEnd`]}
                                  format="HH:mm"
                                  placeholder="Break end"
                                  onChange={(time) => handleTimeChange(day, 'BreakEnd', time)}
                                />
                              </Form.Item>
                            </Col>
                          )}
                          <Col span={12}>
                            <Form.Item name={`${day}End`} noStyle>
                              <TimePicker
                                value={timeValues[`${day}End`]}
                                format="HH:mm"
                                placeholder="End time"
                                onChange={(time) => handleTimeChange(day, 'End', time)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                      {['Takeaway', ...(deliveryEnabled ? ['Delivery'] : [])].map((service) => (
                        <Col xs={24} md={8} key={service}>
                          <Row gutter={[8, 8]}>
                          <Col span={12}>
                            <Form.Item name={`${day}${service}Start`} noStyle>
                              <TimePicker
                                format="HH:mm"
                                placeholder={`${service} start`}
                                onChange={(time) => handleTimeChange(day, `${service}Start`, time)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name={`${day}${service}End`} noStyle>
                              <TimePicker
                                format="HH:mm"
                                placeholder={`${service} end`}
                                onChange={(time) => handleTimeChange(day, `${service}End`, time)}
                              />
                            </Form.Item>
                          </Col>
                          </Row>
                        </Col>
                      ))}
                    </Row>
                  </Form.Item>
                ))}
              </div>
            </div>
            
            <Divider orientation="left" orientationMargin="0">
              CRM Member Points
            </Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Enable CRM member points">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Switch
                      checked={crmPointsEnabled}
                      disabled={!crmActivation.active && !crmPointsEnabled}
                      onChange={(checked) => {
                        if (checked && !crmActivation.active) {
                          notification.error({
                            message: 'CRM is not active',
                            description: crmActivation.reason === 'CRM_MEMBER_LEVEL_MISSING'
                              ? 'Create at least one Member Level in CRM before enabling CRM member points.'
                              : `Online Report CRM check failed: ${crmActivation.reason || 'UNKNOWN'}`,
                          });
                          return;
                        }
                        setCrmPointsEnabled(checked);
                        form.setFieldsValue({ CrmPointsEnabled: checked });
                      }}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                    <span style={{ color: crmActivation.active ? '#52c41a' : '#ff4d4f' }}>
                      {crmActivation.active ? 'CRM active' : `CRM unavailable (${crmActivationReasonText(crmActivation.reason)})`}
                    </span>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="CrmEarnPointsPerDollar"
                  label="Points earned per $1"
                  rules={[{ required: true, message: 'Enter the earn rate' }]}
                >
                  <InputNumber
                    min={0.01}
                    precision={2}
                    style={{ width: '100%' }}
                    value={crmEarnPointsPerDollar}
                    onChange={(value) => setCrmEarnPointsPerDollar(value)}
                    disabled={!crmPointsEnabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="CrmRedeemPointsPerDollar"
                  label="Points required to redeem $1"
                  rules={[{ required: true, message: 'Enter the redemption rate' }]}
                >
                  <InputNumber
                    min={0.01}
                    precision={2}
                    style={{ width: '100%' }}
                    value={crmRedeemPointsPerDollar}
                    onChange={(value) => setCrmRedeemPointsPerDollar(value)}
                    disabled={!crmPointsEnabled}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" orientationMargin="0">
              Promotions
            </Divider>
            
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Enable Promotion">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                      checked={promotionEnabled}
                      onChange={(checked) => {
                        setPromotionEnabled(checked);
                        form.setFieldsValue({ PromotionEnable: checked });
                      }}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                    <span style={{ color: promotionEnabled ? '#52c41a' : '#ff4d4f' }}>
                      {promotionEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            {promotionEnabled && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item 
                      name="PromotionTitle" 
                      label="Promotion Title"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value || value.trim() === '') {
                              return Promise.reject(new Error('Please enter promotion title!'));
                            }
                            const wordCount = value.trim().split(/\s+/).length;
                            if (wordCount > 10) {
                              return Promise.reject(new Error('Title cannot exceed 10 words!'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter promotion title (max 10 words)"
                        value={promotionTitle}
                        onChange={(e) => {
                          setPromotionTitle(e.target.value);
                          form.setFieldsValue({ PromotionTitle: e.target.value });
                        }}
                        style={{ maxWidth: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item 
                      name="PromotionDescription" 
                      label="Promotion Description (Supports emoji 😊 and line breaks)"
                      rules={[
                        {
                          max: 255,
                          message: 'Description cannot exceed 255 characters!',
                        },
                      ]}
                      tooltip="You can use emoji, multiple lines, and formatting"
                    >
                      <Input.TextArea
                        rows={5}
                        placeholder="Enter promotion description (max 255 characters)&#10;✨ Supports emoji and multiple lines&#10;🎉 Line 1: Main offer&#10;💰 Line 2: Additional details&#10;📅 Line 3: Valid dates"
                        value={promotionDescription}
                        onChange={(e) => {
                          if (e.target.value.length <= 255) {
                            setPromotionDescription(e.target.value);
                            form.setFieldsValue({ PromotionDescription: e.target.value });
                          }
                        }}
                        showCount
                        maxLength={255}
                        style={{ maxWidth: '800px' }}
                        autoSize={{ minRows: 5, maxRows: 10 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item name="PromotionType" label="Promotion Type">
                      <Select
                        style={{ width: '100%' }}
                        value={promotionType}
                        onChange={(value) => {
                          setPromotionType(value);
                          form.setFieldsValue({ PromotionType: value });
                        }}
                      >
                        <Option value="percentage">Percentage Discount (%)</Option>
                        <Option value="amount">Amount Discount ($)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item 
                      name="PromotionValue" 
                      label={promotionType === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount ($)'}
                      rules={[
                        {
                          required: promotionEnabled,
                          message: 'Please enter discount value!',
                        },
                        {
                          type: 'number',
                          min: 0,
                          message: 'Value must be greater than or equal to 0!',
                        },
                        {
                          validator: (_, value) => {
                            if (promotionType === 'percentage' && value > 100) {
                              return Promise.reject(new Error('Percentage cannot exceed 100%!'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        max={promotionType === 'percentage' ? 100 : undefined}
                        value={promotionValue}
                        onChange={(value) => {
                          setPromotionValue(value);
                          form.setFieldsValue({ PromotionValue: value });
                        }}
                        style={{ width: '100%' }}
                        placeholder={promotionType === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item 
                      name="PromotionMinAmount" 
                      label="Minimum Purchase Amount ($)"
                      rules={[
                        {
                          type: 'number',
                          min: 0,
                          message: 'Amount must be greater than or equal to 0!',
                        },
                      ]}
                      tooltip="Set to 0 for no minimum purchase requirement"
                    >
                      <InputNumber
                        min={0}
                        value={promotionMinAmount}
                        onChange={(value) => {
                          setPromotionMinAmount(value || 0);
                          form.setFieldsValue({ PromotionMinAmount: value || 0 });
                        }}
                        style={{ width: '100%' }}
                        placeholder="Enter minimum amount"
                        prefix="$"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <Divider orientation="left" orientationMargin="0">
              Menu Group Selection
            </Divider>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Enable Menu Group Selection">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                      checked={menuGroupSelectionEnabled}
                      onChange={(checked) => {
                        setMenuGroupSelectionEnabled(checked);
                        form.setFieldsValue({ EnableMenuGroupSelection: checked });
                      }}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                    <span style={{ color: menuGroupSelectionEnabled ? '#52c41a' : '#ff4d4f' }}>
                      {menuGroupSelectionEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            {menuGroupSelectionEnabled && (
              <Row gutter={16}>
                <Col span={24}>
                  <Table
                    columns={menuGroupColumns}
                    dataSource={menuGroups}
                    rowKey="Code"
                    pagination={false}
                    scroll={{ x: 760 }}
                  />
                </Col>
              </Row>
            )}

            <Divider orientation="left" orientationMargin="0">
              Others
            </Divider>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="UsePickupSlip" valuePropName="checked">
                  <Checkbox>Use Pickup Slip</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="DisableOrderTracking" valuePropName="checked">
                  <Checkbox>Disable Customer Order Tracking</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="NeedCustomerInfo" valuePropName="checked">
                  <Checkbox>Need Customer Info (Take-away)</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item name="ShowPriceInCustomerMenu" valuePropName="checked">
                  <Checkbox>Customer Menu Show Price</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="EnableSharedCart"
                  valuePropName="checked"
                >
                  <Checkbox>Enable Shared Cart for Dine-in</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  name="RequireDineInGuestCount"
                  valuePropName="checked"
                >
                  <Checkbox>Require Dine-in Guest Count</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item>
                  <div className="delivery-config-inline">
                    <Form.Item name="EnableDeliveryFunction" valuePropName="checked" noStyle>
                      <Checkbox
                        checked={deliveryEnabled}
                        onChange={(e) => {
                          setDeliveryEnabled(e.target.checked);
                          form.setFieldsValue({ EnableDeliveryFunction: e.target.checked });
                        }}
                      >
                        Enable Delivery Function
                      </Checkbox>
                    </Form.Item>
                    {deliveryEnabled && (
                      <>
                        <Form.Item
                          label="Delivery Base Fee ($)"
                          name="DeliveryBaseFee"
                          rules={[
                            { required: true, message: 'Please enter delivery base fee!' },
                            {
                              validator: (_, value) => (
                                value === undefined || value === null || Number(value) >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Delivery base fee must be 0 or greater!'))
                              ),
                            },
                          ]}
                        >
                          <InputNumber min={0} precision={2} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item
                          label="Delivery Fee Per Km ($)"
                          name="DeliveryFeePerKm"
                          rules={[
                            { required: true, message: 'Please enter delivery fee per km!' },
                            {
                              validator: (_, value) => (
                                value === undefined || value === null || Number(value) >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Delivery fee per km must be 0 or greater!'))
                              ),
                            },
                          ]}
                        >
                          <InputNumber min={0} precision={2} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item
                          label="Delivery Range (km)"
                          name="DeliveryRangeKm"
                          rules={[
                            { required: true, message: 'Please enter delivery range!' },
                            {
                              validator: (_, value) => (
                                value === undefined || value === null || Number(value) >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Delivery range must be 0 or greater!'))
                              ),
                            },
                          ]}
                        >
                          <InputNumber min={0} precision={2} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item
                          label="Minimum Order Amount ($) - 0 means no limit"
                          name="DeliveryMinimumOrderAmount"
                          rules={[
                            { required: true, message: 'Please enter minimum order amount!' },
                            {
                              validator: (_, value) => (
                                value === undefined || value === null || Number(value) >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('Minimum order amount must be 0 or greater!'))
                              ),
                            },
                          ]}
                        >
                          <InputNumber min={0} precision={2} style={{ width: 160 }} />
                        </Form.Item>
                      </>
                    )}
                  </div>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Max Items in Cart (Default 0 means no restrict)" name="MaxItemInCart">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" orientationMargin="0">
              Order Success Reminders
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Order Success (Pay Later) Reminder">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Form.Item name="PayLaterReminderEnable" valuePropName="checked" noStyle>
                      <Switch
                        checked={payLaterReminderEnabled}
                        onChange={(checked) => {
                          setPayLaterReminderEnabled(checked);
                          form.setFieldsValue({ PayLaterReminderEnable: checked });
                        }}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </Form.Item>
                  </div>
                  <Form.Item 
                    name="PayLaterReminderText" 
                    noStyle
                    rules={[
                      {
                        max: 200,
                        message: 'Reminder text cannot exceed 200 characters!',
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="Enter reminder text for pay later orders (max 200 characters)"
                      disabled={!payLaterReminderEnabled}
                      value={payLaterReminderEnabled && !payLaterReminderText ? 'default' : payLaterReminderText}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) {
                          setPayLaterReminderText(e.target.value);
                          form.setFieldsValue({ PayLaterReminderText: e.target.value });
                        }
                      }}
                      showCount
                      maxLength={200}
                      style={{ marginTop: '8px' }}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Order Success (Pay First) Reminder">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Form.Item name="PayFirstReminderEnable" valuePropName="checked" noStyle>
                      <Switch
                        checked={payFirstReminderEnabled}
                        onChange={(checked) => {
                          setPayFirstReminderEnabled(checked);
                          form.setFieldsValue({ PayFirstReminderEnable: checked });
                        }}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </Form.Item>
                  </div>
                  <Form.Item 
                    name="PayFirstReminderText" 
                    noStyle
                    rules={[
                      {
                        max: 200,
                        message: 'Reminder text cannot exceed 200 characters!',
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="Enter reminder text for pay first orders (max 200 characters)"
                      disabled={!payFirstReminderEnabled}
                      value={payFirstReminderEnabled && !payFirstReminderText ? 'default' : payFirstReminderText}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) {
                          setPayFirstReminderText(e.target.value);
                          form.setFieldsValue({ PayFirstReminderText: e.target.value });
                        }
                      }}
                      showCount
                      maxLength={200}
                      style={{ marginTop: '8px' }}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Order Success (Take-away) Reminder">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Form.Item name="TakeawayReminderEnable" valuePropName="checked" noStyle>
                      <Switch
                        checked={takeawayReminderEnabled}
                        onChange={(checked) => {
                          setTakeawayReminderEnabled(checked);
                          form.setFieldsValue({ TakeawayReminderEnable: checked });
                        }}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </Form.Item>
                  </div>
                  <Form.Item 
                    name="TakeawayReminderText" 
                    noStyle
                    rules={[
                      {
                        max: 200,
                        message: 'Reminder text cannot exceed 200 characters!',
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="Enter reminder text for take-away orders (max 200 characters)"
                      disabled={!takeawayReminderEnabled}
                      value={takeawayReminderEnabled && !takeawayReminderText ? 'default' : takeawayReminderText}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) {
                          setTakeawayReminderText(e.target.value);
                          form.setFieldsValue({ TakeawayReminderText: e.target.value });
                        }
                      }}
                      showCount
                      maxLength={200}
                      style={{ marginTop: '8px' }}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" onClick={handleSave}>
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminStoreConfig;
